// A SECOND PERSON AT A PAUSED SHOP GETS THE FORM 10 MORE WAIVERS, ONCE (TW-0286, 2026-10-16) — the paused screen's half.
//
// Chris, 2026-10-08, on a piercer who met Happy Leaf Tattoo's paused form at the counter, tapped Activate, bounced off
// the $50 page and set the shop up again under her own email: "maybe a maxed account should have 10 waivers added or
// something upon request?" · "if multiple artists find a thing independently, we don't want to necessarily lock them
// out, right" · "we could add 10 waivers, let them in, and then maybe it's another chance at a conversion?"
//
// The rule, the write and the owner's note are the promotions service's (services/promotions, Promotions.TopUp; its
// README → The top-up). This file is the two calls the screen makes and nothing else:
//   * topUpOpen — whether to offer it at all: only a clean yes draws the line, so a door that is switched off, closed for
//     this shop, broken or unreachable leaves the paused screen exactly as it was;
//   * askTopUp — the press: the shop and the email the person typed, never anything else of the page.
// A yes reloads the page: the form reads the API live and the static config carries no lock, so the reload IS the form
// working again. `not_paused` reloads too: somebody activated it meanwhile. Every other answer is a sentence on the
// screen (LockedScreen in App.tsx, the words in i18n.ts), and the box stays for another try.
import { PROMOTIONS_URL } from "./config";

/** How many waivers a top-up puts on a page: the promotions service's own number (Promotions.TopUp @waivers). */
export const TOP_UP_WAIVERS = 10;

const PATH = "/v1/top-up";

export type TopUpAnswer = { ok: true } | { ok: false; code: string };

/** Does the door offer it for this shop right now? true only on a clean yes. A simple GET: no body, no content type. */
export async function topUpOpen(subdomain: string, fetchFn: typeof fetch = fetch): Promise<boolean> {
  try {
    const res = await fetchFn(`${PROMOTIONS_URL}${PATH}?subdomain=${encodeURIComponent(subdomain)}`, { method: "GET" });
    if (res.status !== 200) return false;
    const body = await res.json();
    return body?.data?.open === true;
  } catch {
    return false;
  }
}

/** The press: `{ok: true}` when the page has its 10 more, else the door's code (`unreachable` when it did not answer). */
export async function askTopUp(subdomain: string, email: string, fetchFn: typeof fetch = fetch): Promise<TopUpAnswer> {
  try {
    const res = await fetchFn(`${PROMOTIONS_URL}${PATH}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ subdomain, email }),
    });
    const body = await res.json().catch(() => null);
    if (res.status === 200 && body?.data?.topped_up === true) return { ok: true };
    const code = body?.error?.code;
    return { ok: false, code: typeof code === "string" && code ? code : "unreachable" };
  } catch {
    return { ok: false, code: "unreachable" };
  }
}

/** Does this answer mean the page works again (so the screen reloads)? A yes, or `not_paused`. */
export function worksAgain(answer: TopUpAnswer): boolean {
  return answer.ok || answer.code === "not_paused";
}

/** The reload, on an object a test can stub (jsdom has no navigation). */
export const page = { reload: (): void => window.location.reload() };
